import { keepPreviousData } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { App } from 'antd';
import { trpc } from '../lib/api';
import type {
  ChecklistItem,
  ApprovalSettings,
  WorkOrderTemplate
} from '../../../shared/types/work-order-template.types';

type WorkOrderTemplateEquipment = {
  equipmentId: string;
  equipmentSpecificInstructions?: string | null;
  isMandatory?: boolean;
};

type TemplateUserSummary = {
  id: string;
  name?: string | null;
  email?: string | null;
  preferences?: Record<string, unknown> | null;
  avatarUrl?: string | null;
};

type WorkTypeCategorySummary = {
  id: string;
  categoryLabel?: string | null;
  color?: string | null;
  icon?: string | null;
};

type WorkOrderTemplateRecord = WorkOrderTemplate & {
  equipments?: WorkOrderTemplateEquipment[];
  createdByUser?: TemplateUserSummary | null;
  updatedByUser?: TemplateUserSummary | null;
  workTypeCategory?: WorkTypeCategorySummary | null;
};

const normalizeTemplateList = (data: unknown): WorkOrderTemplateRecord[] => {
  if (Array.isArray(data)) {
    return data as WorkOrderTemplateRecord[];
  }

  if (data && typeof data === 'object') {
    const record = data as Record<string, unknown>;
    if (Array.isArray(record.data)) {
      return record.data as WorkOrderTemplateRecord[];
    }
    if (Array.isArray(record.json)) {
      return record.json as WorkOrderTemplateRecord[];
    }
  }

  return [];
};
export interface UseWorkOrderTemplatesOptions {
  includeInactive?: boolean;
  includeEquipments?: boolean;
  parentTemplateId?: string | null;
}

export const useWorkOrderTemplates = (options: UseWorkOrderTemplatesOptions = {}) => {
  const { includeInactive, includeEquipments, parentTemplateId } = options;

  const query = trpc.workOrderTemplate.list.useQuery(
    {
      includeInactive,
      includeEquipments,
      parentTemplateId
    },
    {
      placeholderData: keepPreviousData
    }
  );

  const templateList = useMemo(() => {
    return normalizeTemplateList(query.data);
  }, [query.data]);

  return useMemo(
    () => ({
      templates: templateList,
      isLoading: query.isLoading,
      refetch: query.refetch
    }),
    [templateList, query.isLoading, query.refetch]
  );
};

export const useWorkOrderTemplate = (
  id: string | null,
  options: { includeEquipments?: boolean } = {}
) => {
  const query = trpc.workOrderTemplate.getById.useQuery(
    {
      id: id ?? '',
      includeEquipments: options.includeEquipments ?? true
    },
    {
      enabled: Boolean(id)
    }
  );

  return useMemo(
    () => ({
      template: (query.data ?? null) as WorkOrderTemplateRecord | null,
      isLoading: query.isLoading,
      refetch: query.refetch
    }),
    [query.data, query.isLoading, query.refetch]
  );
};

type CreatePayload = {
  templateName: string;
  workTypeCategoryId?: string | null;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  description?: string | null;
  parentTemplateId?: string | null;
  orderIndex?: number;
  instructions?: string | null;
  safetyNotes?: string | null;
  checklistItems?: ChecklistItem[];
  standardHours?: number | null;
  requiredTools?: string[];
  requiredParts?: string[];
  isActive?: boolean;
  requiresApproval?: boolean;
  approvalSettings?: ApprovalSettings;
  equipments?: WorkOrderTemplateEquipment[];
};

type UpdatePayload = Partial<CreatePayload> & { id: string };

export const useWorkOrderTemplateMutations = () => {
  const utils = trpc.useContext();
  const { message } = App.useApp();

  const createMutation = trpc.workOrderTemplate.create.useMutation({
    onSuccess: () => {
      utils.workOrderTemplate.list.invalidate();
      message.success('テンプレートを作成しました');
    },
    onError: (error) => {
      message.error(error.message || 'テンプレートの作成に失敗しました');
    }
  });

  const updateMutation = trpc.workOrderTemplate.update.useMutation({
    onSuccess: () => {
      utils.workOrderTemplate.list.invalidate();
      message.success('テンプレートを更新しました');
    },
    onError: (error) => {
      message.error(error.message || 'テンプレートの更新に失敗しました');
    }
  });

  const deleteMutation = trpc.workOrderTemplate.delete.useMutation({
    onSuccess: () => {
      utils.workOrderTemplate.list.invalidate();
      message.success('テンプレートを削除しました');
    },
    onError: (error) => {
      message.error(error.message || 'テンプレートの削除に失敗しました');
    }
  });

  const createTemplate = useCallback(
    (payload: CreatePayload) => createMutation.mutate(payload),
    [createMutation]
  );

  const updateTemplate = useCallback(
    (payload: UpdatePayload) => updateMutation.mutate(payload),
    [updateMutation]
  );

  const deleteTemplate = useCallback(
    (id: string) => deleteMutation.mutate({ id }),
    [deleteMutation]
  );

  return {
    createTemplate,
    updateTemplate,
    deleteTemplate,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending
  };
};
